import React, { useState, useEffect, useMemo } from 'react';
import { Upload, Image, message } from 'antd';
import { InboxOutlined, DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import styles from './ImageDropZone.module.css';

const { Dragger } = Upload;

// Allowed image MIME types
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
// Max file size in bytes (5MB)
const MAX_SIZE = 5 * 1024 * 1024;

/**
 * ImageDropZone - A drag-and-drop image upload component
 * 
 * Props:
 * - value: File object (for controlled form usage)
 * - onChange: Function called with File or null when image changes
 * - existingUrl: URL of existing image to display as preview
 * - onClearExisting: Function called when user wants to remove existing image
 * - compact: Boolean for smaller UI variant
 * - disabled: Boolean to disable the component
 * - maxSize: Custom max file size in bytes (default 5MB)
 * 
 * Note: This component does NOT upload the file immediately.
 * It stores the File object and passes it to the parent form,
 * which should include it in the FormData when submitting.
 */
const ImageDropZone = ({ 
  value, 
  onChange, 
  existingUrl, 
  onClearExisting,
  compact = false,
  disabled = false,
  maxSize = MAX_SIZE
}) => {
  const { t } = useTranslation();
  const [error, setError] = useState(null);

  // Generate preview URL for the selected file
  const localPreviewUrl = useMemo(() => {
    if (value instanceof File) {
      return URL.createObjectURL(value);
    }
    return null;
  }, [value]);

  // Cleanup object URL when component unmounts or file changes
  useEffect(() => {
    return () => {
      if (localPreviewUrl) {
        URL.revokeObjectURL(localPreviewUrl);
      }
    };
  }, [localPreviewUrl]);

  // The URL to display - local preview takes precedence over existing
  const displayUrl = localPreviewUrl || existingUrl;

  const validateFile = (file) => {
    // Check file type
    if (!ALLOWED_TYPES.includes(file.type)) {
      const msg = t('imageUpload.invalidType', 'Please upload an image file (JPEG, PNG, GIF, WebP, SVG)');
      setError(msg);
      message.error(msg);
      return false;
    }

    // Check file size
    if (file.size > maxSize) {
      const sizeMB = (maxSize / (1024 * 1024)).toFixed(0);
      const msg = t('imageUpload.tooLarge', `Image must be smaller than ${sizeMB}MB`);
      setError(msg);
      message.error(msg);
      return false;
    }

    setError(null);
    return true;
  };

  const handleBeforeUpload = (file) => {
    if (validateFile(file)) {
      onChange?.(file);
    }
    // Return false to prevent automatic upload
    return false;
  };

  const handleRemove = (e) => {
    e?.stopPropagation();
    e?.preventDefault();
    
    if (value) {
      // Remove locally selected file
      onChange?.(null);
    } else if (existingUrl && onClearExisting) {
      // Signal to parent that existing image should be cleared
      onClearExisting();
    }
    setError(null);
  };

  // If we have an image to display (either selected file or existing)
  if (displayUrl) {
    return (
      <div className={`${styles.dropZone} ${compact ? styles.compact : ''}`}>
        <div className={styles.previewContainer}>
          <Image 
            src={displayUrl} 
            alt={t('imageUpload.preview', 'Image preview')} 
            className={styles.previewImage}
            style={{ maxHeight: compact ? 120 : 200, objectFit: 'contain' }}
          />
          {!disabled && (
            <div 
              className={styles.deleteButton}
              onClick={handleRemove}
              title={t('common.remove', 'Remove')}
            >
              <DeleteOutlined />
            </div>
          )}
        </div>
        {error && <div className={styles.errorText}>{error}</div>}
      </div>
    );
  }

  // No image - show upload dropzone
  return (
    <div className={`${styles.dropZone} ${compact ? styles.compact : ''}`}>
      <Dragger
        name="image"
        multiple={false}
        showUploadList={false}
        beforeUpload={handleBeforeUpload}
        accept={ALLOWED_TYPES.join(',')}
        disabled={disabled}
      >
        <div className={styles.uploadContent}>
          <InboxOutlined className={styles.uploadIcon} />
          <p className={styles.uploadText}>
            {t('imageUpload.dragOrClick', 'Click or drag image to upload')}
          </p>
          <p className={styles.uploadHint}>
            {t('imageUpload.supportedFormats', 'Supports: JPEG, PNG, GIF, WebP, SVG (max 5MB)')}
          </p>
        </div>
      </Dragger>
      {error && <div className={styles.errorText}>{error}</div>}
    </div>
  );
};

export default ImageDropZone;
